import { create } from "zustand";
import { fetchPrices } from "@/services/prices";
import { getSocket, onPricesUpdate, onRolePricesUpdated, onProductStatusChanged, onBasePricesUpdated, onPricesOrderChanged } from "@/services/socket";

// Helper function to convert RateContext data to categories format
function convertRateDataToCategories(rateData) {
  if (!rateData || !Array.isArray(rateData) || rateData.length === 0) {
    return [];
  }

  return [
    {
      id: "cat-gold",
      title: "طلا",
      items: rateData.map((product) => {
        // Extract values from buyPrice and sellPrice objects
        const buyPriceValue = typeof product.buyPrice === 'object' && product.buyPrice !== null
          ? product.buyPrice.value
          : product.buyPrice || 0;
        const sellPriceValue = typeof product.sellPrice === 'object' && product.sellPrice !== null
          ? product.sellPrice.value
          : product.sellPrice || 0;

        // If isEnable is false, both buy and sell are disabled
        const productEnabled = product.isEnable !== false;
        const buyEnabled = productEnabled ? (product.buyIsEnable !== false) : false;
        const sellEnabled = productEnabled ? (product.sellIsEnable !== false) : false;

        // productVariableType from API: 1 = weight (وزن دلخواه), 2 = count (تعداد)
        const productVariableType = product.productVariableType;
        const unitType = productVariableType === 2 ? 'COUNT' : 'GRAM18';

        return {
          id: product.id,
          name: product.name,
          buy: String(buyPriceValue),
          sell: String(sellPriceValue),
          buyEnabled,
          sellEnabled,
          isEnable: product.isEnable,
          buyPrice: product.buyPrice,
          sellPrice: product.sellPrice,
          productVariableType,
          unit: product.unit ?? null,
          karat: product.karat ?? null,
          unitType,
          startTime: product.startTime || null,
          endTime: product.endTime || null,
          lastPriceUpdateTime: product.lastUpdate || new Date().toISOString(),
        };
      }),
    },
  ];
}

export const usePricesStore = create((set, get) => ({
  categories: [],
  loading: false,
  error: "",
  initialized: false,
  lastUpdateTime: null,

  init: async () => {
    if (get().initialized) return;
    set({ loading: true, error: "" });
    try {
      const data = await fetchPrices();
      set({ categories: Array.isArray(data) ? data : [], initialized: true, lastUpdateTime: new Date() });
      const socket = getSocket();

      // Listen for individual price updates (basePrice only - recalculate using role-specific premium/spread)
      onPricesUpdate(({ categoryId, itemId, basePrice, lastPriceUpdateTime }) => {
        set((state) => {
          if (!Array.isArray(state.categories)) return {};
          const categories = state.categories.map((c) => ({ ...c, items: c.items.map((i) => ({ ...i })) }));
          const cat = categories.find((c) => c.id === categoryId);
          if (!cat) return {};
          const it = cat.items.find((i) => i.id === itemId);
          if (!it) return {};

          // Update basePrice
          if (basePrice !== undefined) {
            it.basePrice = basePrice;

            // Recalculate buy/sell using role-specific premium/spread from current item
            const base = parseFloat(basePrice || '0') || 0;
            const prem = parseFloat(it.premium || '0') || 0;
            const spr = parseFloat(it.spread || '0') || 0;

            const priceWithPremium = base + prem;
            // Admin perspective: spread decreases buy price
            const buy = Math.max(0, priceWithPremium - spr);
            const sell = Math.max(0, priceWithPremium);

            it.buy = String(buy);
            it.sell = String(sell);
          }

          if (lastPriceUpdateTime !== undefined) it.lastPriceUpdateTime = lastPriceUpdateTime;
          return { categories };
        });
      });

      // Listen for role price updates - silently refresh all prices when role settings change
      onRolePricesUpdated(async ({ memberRoleId, productId }) => {
        // console.log('[Prices] Role price updated, refreshing prices...', { memberRoleId, productId });

        // Silently refresh prices from server
        try {
          set({ loading: true });
          const data = await fetchPrices();
          set({ categories: Array.isArray(data) ? data : [], loading: false, lastUpdateTime: new Date() });
        } catch (e) {
          // console.error('[Prices] Error refreshing prices:', e);
          set({ error: "خطا در به‌روزرسانی قیمت‌ها", loading: false });
        }
      });

      // Listen for product status changes - silently refresh prices when product is activated/deactivated
      onProductStatusChanged(async ({ productId, isActive }) => {
        // console.log('[Prices] Product status changed, refreshing prices...', { productId, isActive });

        // Silently refresh prices from server
        try {
          set({ loading: true });
          const data = await fetchPrices();
          set({ categories: Array.isArray(data) ? data : [], loading: false, lastUpdateTime: new Date() });
        } catch (e) {
          // console.error('[Prices] Error refreshing prices:', e);
          set({ error: "خطا در به‌روزرسانی قیمت‌ها", loading: false });
        }
      });

      // Listen for base price updates - update prices for users without role prices
      onBasePricesUpdated(({ categoryId, itemId, buy, sell, basePrice, premium, spread, startTime, endTime, buyEnabled, sellEnabled, lastPriceUpdateTime }) => {
        // console.log('[Prices] Base price updated, updating prices...', { categoryId, itemId, buy, sell });

        set((state) => {
          if (!Array.isArray(state.categories)) return {};
          const categories = state.categories.map((c) => ({ ...c, items: c.items.map((i) => ({ ...i })) }));
          const cat = categories.find((c) => c.id === categoryId);
          if (!cat) return {};
          const it = cat.items.find((i) => i.id === itemId);
          if (!it) return {};

          // Update price fields
          if (buy !== undefined) it.buy = buy;
          if (sell !== undefined) it.sell = sell;
          if (basePrice !== undefined) it.basePrice = basePrice;
          if (premium !== undefined) it.premium = premium;
          if (spread !== undefined) it.spread = spread;
          if (startTime !== undefined) it.startTime = startTime;
          if (endTime !== undefined) it.endTime = endTime;
          if (buyEnabled !== undefined) it.buyEnabled = buyEnabled;
          if (sellEnabled !== undefined) it.sellEnabled = sellEnabled;
          if (lastPriceUpdateTime !== undefined) it.lastPriceUpdateTime = lastPriceUpdateTime;

          return { categories };
        });
      });

      // Listen for order changes - refresh prices to get new order
      onPricesOrderChanged(async () => {
        // console.log('[Prices] Product order changed, refreshing prices...');

        // Silently refresh prices from server to get new order
        try {
          set({ loading: true });
          const data = await fetchPrices();
          set({ categories: Array.isArray(data) ? data : [], loading: false, lastUpdateTime: new Date() });
        } catch (e) {
          // console.error('[Prices] Error refreshing prices after order change:', e);
          set({ error: "خطا در به‌روزرسانی قیمت‌ها", loading: false });
        }
      });

      // Listen for role changes - reload prices when user's role changes
      const handleRoleChanged = async ({ userId, oldRoleId, newRoleId, message }) => {
        // console.log('[Prices] User role changed, reloading prices...', { userId, oldRoleId, newRoleId });

        // Reload prices from server to get new role-specific prices
        try {
          set({ loading: true });
          const data = await fetchPrices();
          set({ categories: Array.isArray(data) ? data : [], loading: false, lastUpdateTime: new Date() });
        } catch (e) {
          // console.error('[Prices] Error refreshing prices after role change:', e);
          set({ error: "خطا در به‌روزرسانی قیمت‌ها", loading: false });
        }
      };

      socket.on('user:role-changed', handleRoleChanged);
    } catch (e) {
      set({ error: "خطا در دریافت قیمت‌ها" });
    } finally {
      set({ loading: false });
    }
  },

  refresh: async () => {
    set({ loading: true, error: "" });
    try {
      const data = await fetchPrices();
      set({ categories: Array.isArray(data) ? data : [], lastUpdateTime: new Date() });
    } catch (e) {
      set({ error: "خطا در دریافت قیمت‌ها" });
    } finally {
      set({ loading: false });
    }
  },

  // Update prices from RateContext data
  updateFromRateContext: (rateData) => {
    if (!rateData || !Array.isArray(rateData)) {
      return;
    }
    const categories = convertRateDataToCategories(rateData);
    // console.log("[PricesStore] Converted categories:", categories);
    // console.log("[PricesStore] Items:", categories[0]?.items?.map(i => ({ id: i.id, name: i.name, buy: i.buy, sell: i.sell })));
    set({ categories, lastUpdateTime: new Date() });
  },
}));


